/* Softbloom product form — self-contained variant picker + AJAX add-to-cart.
   Models the behaviour of assets/product-form.js without importing it. */
if (!customElements.get('sb-product-form')) {
  customElements.define(
    'sb-product-form',
    class SbProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.idInput = this.querySelector('[name="id"]');
        // Button is type="button" (so no form `submit` event fires); find it by hook.
        this.submitButton = this.querySelector('[data-sb-submit], [type="submit"]');
        this.priceEl = this.querySelector('[data-sb-price]');
        this.compareEl = this.querySelector('[data-sb-compare]');
        // Alternate compare-at price (from a product metafield) for A/B testing the
        // strikethrough. Rendered alongside the real one but hidden by default; the
        // experiment toggles which is shown. We only keep its amount in sync here.
        this.compareAltEl = this.querySelector('[data-sb-compare-alt]');
        this.btnLabel = this.submitButton && this.submitButton.querySelector('[data-sb-btn-label]');
        // Running total shown on the button ("Add to cart | $998"), hidden when
        // there's nothing addable (sold out / unavailable).
        this.btnTotal = this.submitButton && this.submitButton.querySelector('[data-sb-btn-total]');
        this.btnSep = this.submitButton && this.submitButton.querySelector('[data-sb-btn-sep]');
        this.etaWindow = this.querySelector('[data-sb-eta-window]');
        const etaEl = this.querySelector('[data-sb-eta]');
        this.etaIsUK = !!etaEl && etaEl.dataset.sbEtaUk === 'true';

        this.variants = this.readJSON('[data-sb-variants]') || [];
        this.optionInputs = Array.from(this.querySelectorAll('[data-sb-option]'));
        // Add-ons are separate products (e.g. a mattress) added as their own cart
        // line item; they don't take part in variant resolution.
        this.addonInputs = Array.from(this.querySelectorAll('[data-sb-addon]'));

        this.moneyFormat = window.sbMoneyFormat || '${{amount}}';

        this.optionInputs.forEach((input) =>
          input.addEventListener('change', () => this.onOptionChange())
        );
        // Re-price (base variant + selected add-ons) when an add-on changes.
        this.addonInputs.forEach((input) =>
          input.addEventListener('change', () => this.updatePrice(this.currentVariant))
        );
        // Drive add-to-cart from a button CLICK, not a form submit. The submit
        // button is type="button", so no `submit` event is emitted — that stops
        // other scripts (e.g. Rebuy Smart Cart) from also hijacking the submit
        // and adding the form's product a second time.
        if (this.submitButton) this.submitButton.addEventListener('click', (e) => this.onSubmit(e));

        // Keep each "Label: value" in sync with the selected option (demo + live).
        this.bindPickerLabels();

        // Demo/hardcoded mode: no variant data → leave the server-rendered
        // price and button label as-is (don't disable add-to-cart).
        if (this.variants.length) this.onOptionChange();
        // Only user-driven changes (after this initial sync) update the URL.
        this.syncUrl = true;
      }

      /* Update the legend value (e.g. "Color: Buttercream") when a radio changes. */
      bindPickerLabels() {
        this.querySelectorAll('[data-sb-picker]').forEach((picker) => {
          const valueEl = picker.querySelector('[data-sb-picker-value]');
          if (!valueEl) return;
          picker.addEventListener('change', (e) => {
            const radio = e.target.closest('input[type="radio"]');
            if (radio) valueEl.textContent = radio.dataset.value || radio.value;
          });
        });
      }

      readJSON(selector) {
        const el = this.querySelector(selector);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return null;
        }
      }

      /* Currently selected option value per option position (1-based). */
      selectedOptions() {
        const values = [];
        this.optionInputs.forEach((input) => {
          const checked =
            input.type === 'radio' || input.type === 'checkbox' ? input.checked : true;
          if (checked) values[Number(input.dataset.optionPosition) - 1] = input.value;
        });
        return values;
      }

      findVariant(selected) {
        return this.variants.find((variant) =>
          (variant.options || []).every((opt, i) => opt === selected[i])
        );
      }

      onOptionChange() {
        const selected = this.selectedOptions();
        const variant = this.findVariant(selected);
        this.currentVariant = variant;

        // Reflect the selected state on labels (border highlight is CSS :checked,
        // but tiles/swatches use sibling labels so toggle a class for robustness).
        this.optionInputs.forEach((input) => {
          const label = input.closest('label') || this.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-selected', input.checked);
        });

        if (!variant) {
          this.setUnavailable(window.sbStrings ? window.sbStrings.unavailable : 'Unavailable');
          return;
        }

        if (this.idInput) this.idInput.value = variant.id;
        this.updatePrice(variant);
        this.updateButton(variant);
        this.updateEta(variant);
        this.updateURL(variant);
      }

      /* Mirror normal product pages: keep ?variant= in the URL in sync with the
         selection so links/refreshes land on the chosen variant. Other query
         params (e.g. UTMs) are preserved. */
      updateURL(variant) {
        if (!this.syncUrl || !variant || !window.history.replaceState) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      /* Estimated delivery window — mirrors snippets/nb-estimated-delivery.liquid:
         ship = now + days_until_ship; show (ship+4 .. ship+10) days, +10 for UK. */
      updateEta(variant) {
        if (!this.etaWindow || !variant || variant.days_until_ship == null) return;
        let days = variant.days_until_ship || 14;
        if (this.etaIsUK) days += 10;
        const day = 86400000;
        const ship = Date.now() + days * day;
        const min = new Date(ship + 4 * day);
        const max = new Date(ship + 10 * day);
        const weeks = Math.ceil((days + 1) / 7);
        const fmt = (d) =>
          d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        this.etaWindow.textContent = `Between ${fmt(min)} and ${fmt(max)} (~${weeks} weeks)`;
      }

      /* Sum of the selected add-on variant prices, in cents. */
      addonTotal() {
        return this.addonInputs.reduce((sum, input) => {
          if (input.checked && input.dataset.sbAddonPrice) {
            return sum + (parseInt(input.dataset.sbAddonPrice, 10) || 0);
          }
          return sum;
        }, 0);
      }

      updatePrice(variant) {
        // Called on add-on change too — without a resolved variant there's no base
        // price to add to (demo mode), so leave the server-rendered price alone.
        if (!variant) return;
        // The headline price and both compare-at amounts show the BASE variant only.
        // Add-ons are reflected on the add-to-cart button total instead.
        if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);
        if (this.btnTotal) {
          this.btnTotal.textContent = this.formatMoneyShort(variant.price + this.addonTotal());
        }
        if (this.compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            this.compareEl.textContent = this.formatMoney(variant.compare_at_price);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
        // The alternate (metafield) compare-at amount is fixed to the base product.
        // Its visibility is controlled by CSS / the A/B test, NOT here — we never toggle
        // its `hidden` state, only its text.
        if (this.compareAltEl) {
          const altBase = parseInt(this.compareAltEl.dataset.sbCompareBase, 10) || 0;
          if (altBase) this.compareAltEl.textContent = this.formatMoney(altBase);
        }
      }

      updateButton(variant) {
        if (!this.submitButton) return;
        if (variant.available) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.disabled = false;
          if (this.btnLabel) this.btnLabel.textContent = window.sbStrings ? window.sbStrings.addToCart : 'Add to cart';
          this.showTotal(true);
        } else {
          this.setUnavailable(window.sbStrings ? window.sbStrings.soldOut : 'Sold out');
        }
      }

      setUnavailable(text) {
        if (!this.submitButton) return;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.disabled = true;
        if (this.btnLabel) this.btnLabel.textContent = text;
        // "Sold out" / "Unavailable" reads wrong next to a price.
        this.showTotal(false);
      }

      showTotal(show) {
        if (this.btnTotal) this.btnTotal.hidden = !show;
        if (this.btnSep) this.btnSep.hidden = !show;
      }

      formatMoney(cents) {
        // Mirror Shopify's {{ amount }} money_format substitution.
        const amount = (cents / 100).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const amountNoDecimals = Math.round(cents / 100).toLocaleString();
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/g, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount);
      }

      /* Like Shopify's `money_without_trailing_zeros` — "$998" rather than "$998.00". */
      formatMoneyShort(cents) {
        if (cents % 100 !== 0) return this.formatMoney(cents);
        const whole = Math.round(cents / 100).toLocaleString();
        return this.moneyFormat.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, whole);
      }

      async onSubmit(e) {
        e.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);

        const formData = new FormData(this.form);
        const quantity = Number(formData.get('quantity') || 1);

        // Main product first, then any selected add-on products as their own lines.
        const items = [{ id: formData.get('id'), quantity }];
        this.addonInputs.forEach((input) => {
          if (input.checked && input.dataset.sbAddonId) {
            items.push({ id: input.dataset.sbAddonId, quantity });
          }
        });

        const body = {
          items,
          sections: ['sb-cart-drawer'],
          sections_url: window.location.pathname,
        };

        try {
          const res = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify(body),
          });
          const data = await res.json();

          if (data.status) {
            // Shopify error payload (e.g. sold out).
            this.showError(data.description || (window.sbStrings && window.sbStrings.cartError));
            return;
          }

          // Buy-now: skip the drawer and head straight to checkout.
          if (this.hasAttribute('data-sb-checkout')) {
            const checkoutUrl = (window.routes.cart_url || '/cart').replace(/\/cart$/, '/checkout');
            window.location.href = checkoutUrl;
            return;
          }

          const html = data.sections && data.sections['sb-cart-drawer'];
          if (html) document.dispatchEvent(new CustomEvent('sb:cart:render', { detail: { html } }));
          document.dispatchEvent(new CustomEvent('sb:cart:open'));
        } catch (err) {
          // Graceful fallback: hand off to the full cart page.
          window.location.href = window.routes.cart_url;
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      showError(message) {
        const errorEl = this.querySelector('[data-sb-form-error]');
        if (errorEl) {
          errorEl.textContent = message || '';
          errorEl.hidden = !message;
        }
      }
    }
  );
}

/* Keep any bag-count badges in sync with the drawer. */
document.addEventListener('sb:cart:count', (e) => {
  const count = e.detail && e.detail.count;
  document.querySelectorAll('[data-sb-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = !count;
  });
});
